import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, TextField, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, Collapse, IconButton, Autocomplete, Button, Dialog, DialogContent, DialogTitle,
  Chip, Card, CardContent, Avatar, Tooltip, TableContainer, Paper, Fade, Zoom, Stack, Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import TradeFormFields from './TradeFormFields';

const TradeHistoryTable = ({ trades = [], onEditTrade, onUpdateTrade, onRefresh, strategies = [] }) => {
  const [editingTradeId, setEditingTradeId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [previewImage, setPreviewImage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [symbolOptions, setSymbolOptions] = useState([]);

  useEffect(() => {
    if (trades && trades.length > 0) {
      const symbols = [...new Set(trades.map(t => t?.symbol).filter(Boolean))];
      setSymbolOptions(symbols);
    }
  }, [trades]);

  const sortedTrades = [...(trades || [])].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    const valA = a[key]?.toString().toLowerCase();
    const valB = b[key]?.toString().toLowerCase();
    if (valA < valB) return direction === 'asc' ? -1 : 1;
    if (valA > valB) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const strategyOptions = [...new Set((trades || []).flatMap(t => t?.strategyNames || []))];

  const startEdit = useCallback((trade) => {
    setEditingTradeId(trade.tradeId);
    setEditValues({ 
      ...trade, 
      _originalImageUrl: trade.imageUrl, 
      _originalScreenshot: trade.screenshot 
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingTradeId(null);
    setEditValues({});
  }, []);

  const saveEdit = async () => {
    try {
      const payload = { ...editValues };
      if (!payload.tradeId) payload.tradeId = editingTradeId;

      // Always preserve original screenshot and imageUrl if they exist
      if (payload._originalScreenshot) {
        payload.screenshot = payload._originalScreenshot;
      }
      if (payload._originalImageUrl) {
        payload.imageUrl = payload._originalImageUrl;
      }
      
      // Calculate profit/loss based on manual override or auto-calculation
      if (!payload.manualPnL) {
        const reward = parseFloat(payload.rewardAmount);
        const risk = parseFloat(payload.riskAmount);
        const status = payload.status;

        if (!isNaN(reward) && !isNaN(risk)) {
          payload.profitLoss =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' :
            '';
        }
      }
      payload.pnl = payload.profitLoss;

      // Clean up temporary fields
      delete payload._originalImageUrl;
      delete payload._originalScreenshot;
      delete payload._profitLossChanged;
      
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (typeof onUpdateTrade === 'function') onUpdateTrade(payload);
    } catch (error) {
      console.error('Error updating trade:', error);
    } finally {
      cancelEdit();
    }
  };

  const handleDelete = async (tradeId) => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/deleteTrade', {
        tradeId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: 'Trade Deleted', severity: 'success' });
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      console.error('Error deleting trade', error);
    }
  };

  const handleFieldChange = React.useCallback((key, value) => {
    setEditValues((prev) => {
      const updated = { ...prev, [key]: value };
      
      // Only auto-calculate P&L if manual override is not enabled
      if (!updated.manualPnL && key !== 'profitLoss') {
        const reward = parseFloat(key === 'rewardAmount' ? value : updated.rewardAmount);
        const risk = parseFloat(key === 'riskAmount' ? value : updated.riskAmount);
        const status = key === 'status' ? value : updated.status;
        
        if (!isNaN(reward) && !isNaN(risk)) {
          const pnl =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' : '';
          updated.profitLoss = pnl;
          updated.pnl = pnl;
          updated._profitLossChanged = true;
        }
      }
      
      return updated;
    });
  }, []);

  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);

  const handleOpenImage = (url) => {
    setSelectedImage(url);
    setOpenImageModal(true);
  };

  const handleCloseImage = () => {
    setOpenImageModal(false);
    setSelectedImage(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WIN': return 'success';
      case 'LOSS': return 'error';
      case 'BREAKEVEN': return 'warning';
      case 'OPEN': return 'info';
      case 'ANALYSIS': return 'default';
      default: return 'default';
    }
  };

  const getPnLIcon = (pnl) => {
    const value = parseFloat(pnl);
    if (value > 0) return <TrendingUpIcon fontSize="small" />;
    if (value < 0) return <TrendingDownIcon fontSize="small" />;
    return <RemoveIcon fontSize="small" />;
  };

  const formatPnL = (pnl) => {
    const value = parseFloat(pnl);
    if (isNaN(value)) return pnl;
    return value >= 0 ? `+$${Math.abs(value).toFixed(2)}` : `-$${Math.abs(value).toFixed(2)}`;
  };

  return (
    <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ 
          p: 3, 
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📊 Trade History
            <Chip 
              label={`${(trades || []).length} trades`} 
              size="small" 
              sx={{ 
                bgcolor: 'primary.dark', 
                color: 'primary.contrastText',
                fontWeight: 'bold'
              }} 
            />
          </Typography>
        </Box>
        
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                '& .MuiTableCell-head': {
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  borderBottom: '2px solid',
                  borderColor: 'primary.main'
                }
              }}>
                {["symbol", "status", "strategy", "tradeDate", "profitLoss", "imageUrl"].map((key) => {
                  const label = {
                    symbol: "Symbol", status: "Status", strategy: "Strategy",
                    tradeDate: "Date", profitLoss: "P&L", imageUrl: "Chart"
                  }[key];
                  return (
                    <TableCell
                      key={key}
                      onClick={() => setSortConfig(prev => ({
                        key,
                        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
                      }))}
                      sx={{ 
                        cursor: 'pointer', 
                        userSelect: 'none',
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {label}
                        {sortConfig.key === key && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTrades.map((t, index) => (
                <React.Fragment key={t.tradeId}>
                  <Fade in timeout={300 + index * 50}>
                    <TableRow 
                      hover 
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'action.hover',
                          transform: 'scale(1.001)',
                          transition: 'all 0.2s ease-in-out'
                        },
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: 'primary.main',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {t.symbol?.substring(0, 2)}
                          </Avatar>
                          <Typography variant="body2" fontWeight="medium">
                            {t.symbol}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t.status}
                          color={getStatusColor(t.status)}
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontWeight: 'bold',
                            minWidth: 80,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {(t.strategyNames || []).slice(0, 2).map((strategy, idx) => (
                            <Chip
                              key={idx}
                              label={strategy}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ))}
                          {(t.strategyNames || []).length > 2 && (
                            <Chip
                              label={`+${(t.strategyNames || []).length - 2}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(t.tradeDate).format('MMM DD, YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: t._profitLossChanged ? 'warning.light' : 'transparent',
                          color: parseFloat(t.profitLoss) >= 0 ? 'success.main' : 'error.main'
                        }}>
                          {getPnLIcon(t.profitLoss)}
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            sx={{ 
                              fontFamily: 'monospace',
                              fontSize: '0.875rem'
                            }}
                          >
                            {formatPnL(t.profitLoss)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {t.imageUrl || t.screenshot ? (
                          <Tooltip title="View Chart">
                            <IconButton 
                              size="small" 
                              onClick={() => setPreviewImage(t.imageUrl || `data:image/png;base64,${t.screenshot}`)}
                              sx={{ 
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': { bgcolor: 'primary.dark' }
                              }}
                            >
                              <ImageIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit Trade">
                          <IconButton 
                            onClick={() => startEdit(t)} 
                            size="small"
                            sx={{
                              bgcolor: 'action.hover',
                              '&:hover': { 
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </Fade>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                      <Collapse in={editingTradeId === t.tradeId} timeout={400}>
                        <Box sx={{ 
                          p: 3, 
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', 
                          borderRadius: 2, 
                          m: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="h6" gutterBottom sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            color: 'primary.main',
                            fontWeight: 'bold'
                          }}>
                            ✏️ Edit Trade Details
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          
                          <TradeFormFields
                            values={editValues}
                            onChange={handleFieldChange}
                            strategies={strategies}
                            trades={trades}
                          />

                          <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            justifyContent: 'flex-end',
                            mt: 3,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                          }}>
                            <Button
                              variant="outlined"
                              onClick={cancelEdit}
                              startIcon={<CancelIcon />}
                              sx={{ minWidth: 100 }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={saveEdit}
                              startIcon={<SaveIcon />}
                              sx={{ minWidth: 100 }}
                            >
                              Save
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleDelete(editValues.tradeId)}
                              startIcon={<DeleteIcon />}
                              sx={{ minWidth: 100 }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
            </React.Fragment>
          ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {(trades || []).length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            color: 'text.secondary'
          }}>
            <Typography variant="h6" gutterBottom>
              📈 No trades found
            </Typography>
            <Typography variant="body2">
              Start by adding your first trade to see it here
            </Typography>
          </Box>
        )}
      </CardContent>

      <Dialog 
        open={!!previewImage} 
        onClose={() => setPreviewImage('')} 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Typography variant="h6">📊 Trade Chart</Typography>
          <IconButton 
            onClick={() => setPreviewImage('')}
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: 400
          }}>
            <img 
              src={previewImage} 
              alt="Trade Screenshot" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '80vh',
                borderRadius: 8
              }} 
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TradeHistoryTable;
